import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useNavigate,
  useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { trackSearch,
  trackProfileView } from '../services/analytics';
import AppLayout from './AppLayout.jsx';
import { useProductTour } from '../hooks/useProductTour';
import { bandaComunidadSteps } from '../tours/bandaComunidadTour';
import PublicProfilePreview from './PublicProfilePreview.jsx';
import PublicProfilePreviewCofradia from './PublicProfilePreviewCofradia.jsx';
import { bandasAPI,
  cofradiasAPI,
  catalogoAPI } from '../services/api';
import { getPerfilDisplayName } from '../utils/miEntidadNombreDefecto';
import { buildPublicProfileShareUrl } from '../utils/publicProfileShareUrl.js';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Input,
  Button,
  NativeSelect,
  NumberInput,
  Icon,
  IconButton,
  Badge,
  Card,
  SimpleGrid,
  Flex,
  Menu,
  Collapsible,
  useDisclosure,
  Spinner,
  Alert,
  Stack,
  Textarea,
  Field,
  Portal,
  Dialog,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import EntityLogoImage from './EntityLogoImage.jsx';
import { RangeSlider, RangeSliderTrack, RangeSliderFilledTrack, RangeSliderThumb, Divider, SliderRoot, SliderTrack, SliderFilledTrack, SliderThumb } from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { InputGroup, InputLeftElement, InputRightElement } from '@/components/ui/input-group';
import { Tooltip } from '@/components/ui/tooltip';
import {
  FaSearch,
  FaFilter,
  FaSortAmountDown,
  FaSortAmountUp,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaMusic,
  FaChurch,
  FaHeart,
  FaEye,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaAdjust,
  FaStar,
  FaCheckCircle,
  FaBuilding,
} from 'react-icons/fa';

/** Texto de la etiqueta de tipo en tarjetas de Comunidad (no genérico «Banda» / «Cofradía»). */
function getCommunityCardTypeLabel(item) {
  if (item.type === 'banda') {
    return (item.tipoGrupo && String(item.tipoGrupo).trim()) || 'Banda';
  }
  const fromTipo = item.tipoEntidadNombre && String(item.tipoEntidadNombre).trim();
  const fromLegacy = item.tipoGrupo && String(item.tipoGrupo).trim();
  return fromTipo || fromLegacy || 'Entidad contratante';
}

function getCommunityCardTypeIcon(item) {
  if (item.type === 'banda') return FaMusic;
  const cod = (item.tipoEntidadCodigo || '').toLowerCase();
  if (cod === 'ayuntamiento' || cod === 'diputacion' || cod === 'comunidad_autonoma') return FaBuilding;
  return FaChurch;
}

const Search = ({ 
  userType = 'banda', 
  inModal = false, 
  eventDate = null,
  onSelectResult = null,
  shortlistedIds = [],
  onAddToShortlist = null,
  eventName = '', // Nombre del evento para mostrarlo en el modal
  eventLocation = '', // Ubicación del evento
  eventLat = null, // Latitud del evento
  eventLng = null, // Longitud del evento
  eventPrice = null, // Precio ofrecido
  eventNotes = '' // Notas adicionales
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, isAuthenticated } = useAuth();
  useProductTour('banda-comunidad', bandaComunidadSteps, {
    autoStart: false,
    listenGlobal: !inModal && userType === 'banda',
  });
  const guestPublicCommunityHeader = !isAuthenticated && location.pathname === '/comunidad';
  const searchHeaderVariant = guestPublicCommunityHeader ? 'guestPublic' : 'default';
  
  const { open: isFiltersOpen, onToggle: onFiltersToggle } = useDisclosure();
  const { open: isProfileOpen, onOpen: onProfileOpen, onClose: onProfileClose } = useDisclosure();
  const { open: isContactModalOpen, onOpen: onContactModalOpen, onClose: onContactModalClose } = useDisclosure();
  const {
    open: isGuestProximityModalOpen,
    onOpen: onGuestProximityModalOpen,
    onClose: onGuestProximityModalClose,
  } = useDisclosure();
  
  // Estado para el perfil seleccionado (para mostrar en modal)
  const [selectedProfile, setSelectedProfile] = useState(null);
  
  // Estado para el modal de contacto
  const [selectedBandaForContact, setSelectedBandaForContact] = useState(null);
  const [contactMessage, setContactMessage] = useState('');
  const [isContactingBanda, setIsContactingBanda] = useState(false);
  
  // Estado para manejar errores
  const [hasError, setHasError] = useState(false);
  
  /** Desde "Buscar banda" para un evento solo tienen sentido bandas (el desplegable de tipo está oculto). */
  const getDefaultCommunityEntityKind = () => (eventName ? 'banda' : '');
  
  // Estados para búsqueda y filtros
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    dateMode: 'single', // 'single' o 'range'
    singleDate: eventDate || '', // Si hay eventDate, usarlo
    dateRange: { start: '', end: '' },
    /** '' = todos, 'banda' = solo bandas, 'contratante' = entidades contratantes (cofradías / promotores) */
    communityEntityKind: getDefaultCommunityEntityKind(),
    /** Filtro fino de tipo de entidad (catálogo); solo aplica con communityEntityKind === 'contratante' */
    contratanteTipoEntidadId: '',
    bandType: '', // solo para bandas
    proximity: 1200, // km - por defecto sin límite (toda la península)
  });

  const [tiposEntidadContratante, setTiposEntidadContratante] = useState([]);

  useEffect(() => {
    let cancelled = false;
    catalogoAPI
      .getTiposEntidadCadencya()
      .then((res) => {
        const list = res.data || [];
        const sinBanda = list.filter((t) => (t.codigo || '').toLowerCase() !== 'banda');
        if (!cancelled) setTiposEntidadContratante(sinBanda);
      })
      .catch(() => {
        if (!cancelled) setTiposEntidadContratante([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  
  // Estado intermedio para el slider de proximidad (para debounce)
  const [proximitySlider, setProximitySlider] = useState(1200);

  /** Referencia de mapa para invitados (sin login) cuando no hay evento con coordenadas */
  const [guestRefLat, setGuestRefLat] = useState(null);
  const [guestRefLng, setGuestRefLng] = useState(null);
  const [guestRefLabel, setGuestRefLabel] = useState('');
  const [proximityModalQuery, setProximityModalQuery] = useState('');
  const [proximityModalSuggestions, setProximityModalSuggestions] = useState([]);
  const [showProximityModalSuggestions, setShowProximityModalSuggestions] = useState(false);
  const [isSearchingProximityModal, setIsSearchingProximityModal] = useState(false);

  // Estados para ordenamiento y resultados (declarados antes de callbacks que puedan referenciarlos indirectamente)
  const [sortBy, setSortBy] = useState('alphabetical_asc');
  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);

  const hasEventCoords =
    eventLat != null &&
    eventLng != null &&
    String(eventLat).trim() !== '' &&
    String(eventLng).trim() !== '' &&
    Number.isFinite(Number(eventLat)) &&
    Number.isFinite(Number(eventLng));
  const hasProfileCoords =
    profile?.lat != null && profile?.lng != null && Number.isFinite(Number(profile.lat)) && Number.isFinite(Number(profile.lng));
  const needsGuestProximityOrigin = !isAuthenticated && !hasEventCoords && !hasProfileCoords;
  const hasGuestProximityRef = guestRefLat != null && guestRefLng != null && Number.isFinite(guestRefLat) && Number.isFinite(guestRefLng);
  const guestProximityBlocked = needsGuestProximityOrigin && !hasGuestProximityRef;

  const searchProximityAddresses = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setProximityModalSuggestions([]);
      setShowProximityModalSuggestions(false);
      return;
    }
    setIsSearchingProximityModal(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&countrycodes=es&addressdetails=1`,
        {
          headers: {
            'Accept-Language': 'es',
            'User-Agent': 'Cadencya/1.0 (https://cadencya.com)',
          },
        },
      );
      if (!response.ok) throw new Error('Error al buscar ubicación');
      const data = await response.json();
      setProximityModalSuggestions(Array.isArray(data) ? data : []);
      setShowProximityModalSuggestions(true);
    } catch (e) {
      console.error('Error buscando dirección (Comunidad):', e);
      setProximityModalSuggestions([]);
      toaster.create({
        title: 'No se pudo buscar',
        description: 'Inténtalo de nuevo en unos segundos.',
        type: 'error',
        duration: 4000,
        closable: true,
        position: 'top',
      });
    } finally {
      setIsSearchingProximityModal(false);
    }
  }, []);

  useEffect(() => {
    if (!isGuestProximityModalOpen) return undefined;
    setProximityModalQuery('');
    setProximityModalSuggestions([]);
    setShowProximityModalSuggestions(false);
    return undefined;
  }, [isGuestProximityModalOpen]);

  useEffect(() => {
    if (!isGuestProximityModalOpen) return undefined;
    const timer = setTimeout(() => {
      const q = proximityModalQuery.trim();
      if (q.length < 3) {
        setProximityModalSuggestions([]);
        setShowProximityModalSuggestions(false);
        return;
      }
      searchProximityAddresses(q);
    }, 500);
    return () => clearTimeout(timer);
  }, [proximityModalQuery, isGuestProximityModalOpen, searchProximityAddresses]);

  const selectGuestProximitySuggestion = useCallback(
    (suggestion) => {
      if (!suggestion?.lat || !suggestion?.lon) {
        toaster.create({
          title: 'Ubicación sin coordenadas',
          description: 'Elige una dirección más concreta (calle, número, ciudad).',
          type: 'warning',
          duration: 5000,
          closable: true,
          position: 'top',
        });
        return;
      }
      const lat = parseFloat(suggestion.lat);
      const lng = parseFloat(suggestion.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        toaster.create({
          title: 'Coordenadas no válidas',
          description: 'Prueba con otra sugerencia de la lista.',
          type: 'warning',
          duration: 4000,
          closable: true,
          position: 'top',
        });
        return;
      }
      setGuestRefLat(lat);
      setGuestRefLng(lng);
      setGuestRefLabel(suggestion.display_name || `${lat}, ${lng}`);
      setProximityModalSuggestions([]);
      setShowProximityModalSuggestions(false);
      onGuestProximityModalClose();
      toaster.create({
        title: 'Ubicación de referencia',
        description: 'Ya puedes usar el filtro de proximidad respecto a esta dirección.',
        type: 'success',
        duration: 3500,
        closable: true,
        position: 'top',
      });
    },
    [onGuestProximityModalClose],
  );

  // Sincronizar slider → filters.proximity (para la API) sin disparar un fetch en cada pixel del drag
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((prev) =>
        prev.proximity === proximitySlider ? prev : { ...prev, proximity: proximitySlider },
      );
    }, 250);
    return () => clearTimeout(t);
  }, [proximitySlider]);

  // Cargar datos desde la API
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const toFinite = (v) => {
          if (v == null || v === '') return null;
          const n = Number(v);
          return Number.isFinite(n) ? n : null;
        };

        const toValidPair = (latV, lngV) => {
          const la = toFinite(latV);
          const ln = toFinite(lngV);
          if (la == null || ln == null) return null;
          if (la < -90 || la > 90) return null;
          if (ln < -180 || ln > 180) return null;
          // En este producto (España), (0,0) suele ser placeholder, no una ubicación real.
          if (Math.abs(la) < 1e-9 && Math.abs(ln) < 1e-9) return null;
          return { lat: la, lng: ln };
        };

        const searchFromEvent = Boolean(eventName && String(eventName).trim());

        // Punto de referencia
        // - Comunidad: perfil (si existe)
        // - Buscar banda desde evento: PERFIL primero (la entidad que busca), y si no hay, evento
        // - Invitado: guestRef
        const eventPair = toValidPair(eventLat, eventLng);
        const profilePair = toValidPair(profile?.lat, profile?.lng);

        let referenceLat = null;
        let referenceLng = null;

        if (searchFromEvent) {
          if (profilePair) {
            referenceLat = profilePair.lat;
            referenceLng = profilePair.lng;
            console.log('👤 (Buscar banda) Usando coordenadas del perfil:', { referenceLat, referenceLng });
          } else if (eventPair) {
            referenceLat = eventPair.lat;
            referenceLng = eventPair.lng;
            console.log('📍 (Buscar banda) Usando coordenadas del evento:', { referenceLat, referenceLng });
          }
        } else {
          if (eventPair) {
            referenceLat = eventPair.lat;
            referenceLng = eventPair.lng;
            console.log('📍 Usando coordenadas del evento:', { referenceLat, referenceLng });
          } else if (profilePair) {
            referenceLat = profilePair.lat;
            referenceLng = profilePair.lng;
            console.log('👤 Usando coordenadas del perfil:', { referenceLat, referenceLng });
          }
        }

        if (
          (referenceLat == null || referenceLng == null) &&
          guestRefLat != null &&
          guestRefLng != null
        ) {
          const guestPair = toValidPair(guestRefLat, guestRefLng);
          if (guestPair) {
            referenceLat = guestPair.lat;
            referenceLng = guestPair.lng;
            console.log('🧭 Usando coordenadas de referencia (invitado):', { referenceLat, referenceLng });
          }
        }
        
        // Construir parámetros de búsqueda para el backend
        const bandaParams = {};
        if (filters.bandType) {
          bandaParams.tipoGrupo = filters.bandType;
        }
        // Filtrar por fecha de disponibilidad si se especifica
        if (filters.dateMode === 'single' && filters.singleDate) {
          bandaParams.disponibleEn = filters.singleDate;
        }
        // Agregar coordenadas y distancia máxima si están disponibles
        console.log('🔍 Verificando coordenadas:', {
          referenceLat,
          referenceLng,
          proximity: filters.proximity,
          tieneCoords: referenceLat != null && referenceLng != null,
        });
        
        if (referenceLat != null && referenceLng != null) {
          bandaParams.lat = referenceLat;
          bandaParams.lng = referenceLng;
          if (filters.proximity < 1200) {
            bandaParams.maxDistance = filters.proximity;
          }
          console.log('✅ Coordenadas agregadas a bandaParams');
        } else {
          console.log('❌ Coordenadas NO agregadas (sin lat/lng válidos para referencia)');
        }
        
        // Parámetros para cofradías (sin fecha de disponibilidad)
        const cofradiaParams = {};
        if (referenceLat != null && referenceLng != null) {
          cofradiaParams.lat = referenceLat;
          cofradiaParams.lng = referenceLng;
          if (filters.proximity < 1200) {
            cofradiaParams.maxDistance = filters.proximity;
          }
        }
        const kind = filters.communityEntityKind;
        if (kind === 'contratante' && filters.contratanteTipoEntidadId) {
          cofradiaParams.tipoEntidadId = filters.contratanteTipoEntidadId;
        }

        const loadBandas = kind === 'banda' || kind === '' || searchFromEvent;
        const loadContratantes = !searchFromEvent && (kind === 'contratante' || kind === '');

        const promises = [];

        console.log('📤 Enviando a API bandas:', bandaParams);

        if (loadBandas) {
          promises.push(
            bandasAPI.getAll(bandaParams).then((res) => {
              const bandas = res.data?.data || res.data || [];
              return bandas.map((b) => ({
                id: b.id,
                userId: b.userId,
                name: b.nombre || 'Sin nombre',
                type: 'banda',
                location: b.localizacion?.ciudad || b.ciudad || 'Sin ubicación',
                province: b.localizacion?.provincia || b.provincia || '',
                description: b.bio || '',
                image: b.avatar || '/banda-background.jpg',
                rating: 4.5,
                verified: b.verificationStatus === 'VERIFIED',
                tipoGrupo: b.tipoGrupo || '',
                bandType: b.tipoGrupo || '',
                nMusicos: b.nMusicos || 0,
                especialidades: b.especialidades || [],
                availableDates: [],
                distance: b.distance !== undefined && b.distance !== null ? b.distance : null,
              }));
            }),
          );
        }

        if (loadContratantes) {
          promises.push(
            cofradiasAPI.getAll(cofradiaParams).then((res) => {
              const cofradias = res.data?.data || res.data || [];
              return cofradias.map((c) => ({
                id: c.id,
                userId: c.userId,
                name: c.nombre || 'Sin nombre',
                type: 'cofradia',
                location: c.localizacion?.ciudad || c.ciudad || 'Sin ubicación',
                province: c.localizacion?.provincia || c.provincia || '',
                description: c.bio || '',
                image: c.avatar || '/cofradia-background.jpg',
                rating: 4.5,
                verified: c.verificationStatus === 'VERIFIED',
                tipoGrupo: c.tipoGrupo || '',
                tipoEntidadId: c.tipoEntidadId || null,
                tipoEntidadNombre: c.tipoEntidad?.nombre || '',
                tipoEntidadCodigo: c.tipoEntidad?.codigo || '',
                numeroHermanos: c.numeroHermanos || 0,
                distance: c.distance !== undefined && c.distance !== null ? c.distance : null,
              }));
            }),
          );
        }
        
        const results = await Promise.all(promises);
        const allData = results.flat();
        console.log('Datos cargados desde API:', allData);
        setResults(allData);
        trackSearch(searchQuery, {
          entity_type: filters.communityEntityKind,
          contratante_tipo_id: filters.contratanteTipoEntidadId || undefined,
          results_count: allData.length,
        });
      } catch (error) {
        console.error('Error cargando datos:', error);
        // Mostrar lista vacía en caso de error
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [
    filters.communityEntityKind,
    filters.contratanteTipoEntidadId,
    filters.bandType,
    filters.singleDate,
    filters.dateMode,
    filters.proximity,
    eventLat,
    eventLng,
    profile,
    guestRefLat,
    guestRefLng,
    eventName,
  ]);

  // Función para aplicar filtros (locales - los principales se aplican en el backend)
  const applyFilters = useCallback(() => {
    try {
      let filtered = [...results];
    
    // Filtro por búsqueda general (local)
    if (searchQuery.trim()) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Nota: El filtro por fecha se aplica en el backend con el parámetro disponibleEn
    // Nota: El filtro por tipo de banda se aplica en el backend con el parámetro tipoGrupo
    
    if (filters.communityEntityKind === 'banda') {
      filtered = filtered.filter((item) => item.type === 'banda');
    } else if (filters.communityEntityKind === 'contratante') {
      filtered = filtered.filter((item) => item.type === 'cofradia');
    }
    if (eventName && String(eventName).trim()) {
      filtered = filtered.filter((item) => item.type === 'banda');
    }

    if (proximitySlider < 1200) {
      filtered = filtered.filter(
        (item) =>
          item.distance != null &&
          Number.isFinite(Number(item.distance)) &&
          Number(item.distance) <= proximitySlider,
      );
    }

    // Aplicar ordenamiento
    switch (sortBy) {
      case 'alphabetical_asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'alphabetical_desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'proximity':
        // Ordenar por distancia (los que no tienen distancia al final)
        filtered.sort((a, b) => {
          if (a.distance === null && b.distance === null) return 0;
          if (a.distance === null) return 1;
          if (b.distance === null) return -1;
          return a.distance - b.distance;
        });
        break;
      case 'trusted_by':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      default:
        break;
    }
    
    setFilteredResults(filtered);
    } catch (error) {
      console.error('Error aplicando filtros:', error);
      setHasError(true);
    }
  }, [results, searchQuery, filters.communityEntityKind, sortBy, eventName, proximitySlider]);

  // Aplicar filtros cuando cambien los valores
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Función para ver el perfil en modal
  const handleViewProfile = (item) => {
    trackProfileView(item.type, item.id);
    // Navegar a la página de perfil público en lugar de abrir un modal
    const profilePath = item.type === 'banda' 
      ? `/ver-perfil-banda?id=${item.id}`
      : `/ver-perfil-cofradia?id=${item.id}`;
    navigate(profilePath);
  };

  // Función para abrir el modal de contacto
  const handleOpenContactModal = (banda) => {
    setSelectedBandaForContact(banda);
    
    // Generar mensaje genérico con información del evento
    let defaultMessage = `¡Hola! Somos una cofradía interesada en contar con vuestra banda para nuestro evento`;
    
    if (eventName) {
      defaultMessage += ` "${eventName}"`;
    }
    defaultMessage += '.';
    
    // Agregar información de fecha
    if (eventDate) {
      const fecha = new Date(eventDate);
      const fechaFormateada = fecha.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      defaultMessage += `\n\n📅 Fecha: ${fechaFormateada}`;
    }
    
    // Agregar ubicación
    if (eventLocation) {
      defaultMessage += `\n📍 Ubicación: ${eventLocation}`;
    }
    
    // Presupuesto eliminado de la UI
    
    // Agregar notas adicionales
    if (eventNotes) {
      defaultMessage += `\n\n📝 Información adicional:\n${eventNotes}`;
    }
    
    defaultMessage += `\n\n¿Estaríais disponibles? Nos encantaría conocer vuestra disponibilidad y condiciones.\n\n¡Esperamos vuestra respuesta!`;
    
    setContactMessage(defaultMessage);
    onContactModalOpen();
  };

  const runAddToShortlist = async (mensaje) => {
    if (!onAddToShortlist || !selectedBandaForContact) return;
    setIsContactingBanda(true);
    try {
      await onAddToShortlist(selectedBandaForContact, mensaje);
      onContactModalClose();
      setSelectedBandaForContact(null);
      setContactMessage('');
    } catch (error) {
      console.error('Error al añadir banda a favoritas:', error);
      toaster.create({
        title: 'Error',
        description: error.message || 'No se pudo completar la acción. Por favor, inténtalo de nuevo.',
        type: 'error',
        duration: 5000,
        closable: true,
      });
    } finally {
      setIsContactingBanda(false);
    }
  };

  /** Añade a favoritas y crea conversación si no existe, sin enviar mensaje al chat. */
  const handleAddFavoritesOnly = () => {
    void runAddToShortlist('');
  };

  // Confirmar contacto: requiere mensaje y lo envía al chat
  const handleConfirmContact = async () => {
    if (!contactMessage.trim()) {
      toaster.create({
        title: 'Mensaje requerido',
        description: 'Por favor, escribe un mensaje para contactar a la banda.',
        type: 'warning',
        duration: 3000,
        closable: true,
      });
      return;
    }
    await runAddToShortlist(contactMessage.trim());
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value };

      if (key === 'communityEntityKind') {
        if (value !== 'banda') {
          newFilters.bandType = '';
          newFilters.singleDate = '';
          newFilters.dateRange = { start: '', end: '' };
        }
        if (value !== 'contratante') {
          newFilters.contratanteTipoEntidadId = '';
        }
      }

      if (key === 'dateMode') {
        newFilters.singleDate = '';
        newFilters.dateRange = { start: '', end: '' };
      }

      return newFilters;
    });
  };

  const clearFilters = () => {
    setFilters({
      dateMode: 'single',
      singleDate: '',
      dateRange: { start: '', end: '' },
      communityEntityKind: getDefaultCommunityEntityKind(),
      contratanteTipoEntidadId: '',
      bandType: '',
      proximity: 1200,
    });
    setProximitySlider(1200); // También resetear el slider
    setGuestRefLat(null);
    setGuestRefLng(null);
    setGuestRefLabel('');
    setSearchQuery('');
    setSortBy('alphabetical_asc');
  };

  const getSortLabel = (sortValue) => {
    switch (sortValue) {
      case 'alphabetical_asc': return 'A-Z';
      case 'alphabetical_desc': return 'Z-A';
      case 'proximity': return 'Más cerca';
      case 'trusted_by': return 'Más confiables';
      default: return 'Ordenar';
    }
  };

  // Si hay un error, mostrar mensaje de error
  if (hasError) {
    return (
      <AppLayout
        headerVariant={searchHeaderVariant}
        userType={userType}
        userName={getPerfilDisplayName(profile, userType)}
      >
        <Container maxW="7xl" py={6}>
          <Alert.Root status="error" rounded="xl">
            <Alert.Indicator />
            Ha ocurrido un error al cargar la página de búsqueda. Por favor, recarga la página.
          </Alert.Root>
        </Container>
      </AppLayout>
    );
  }

  const searchContent = (
    <Container maxW="7xl" py={inModal ? 0 : 6}>
      <VStack gap={6} align="stretch">
        {/* Título - Solo mostrar si no está en modal */}
        {!inModal && (
          <Box textAlign="center" data-tour="comunidad-header">
            <Heading size="xl" mb={2}>
              Comunidad
            </Heading>
            <Text color="gray.600" fontSize="lg">
              {userType === 'banda' 
                ? 'Descubre cofradías profesionales cerca de ti' 
                : 'Descubre bandas profesionales cerca de ti'
              }
            </Text>
          </Box>
        )}

          {/* Barra de búsqueda principal */}
          <Card.Root bg="white" boxShadow="lg" rounded="2xl" data-tour="comunidad-buscar">
            <Card.Body p={6}>
              <VStack gap={4}>
                <InputGroup size="lg">
                  <InputLeftElement>
                    <Icon as={FaSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Buscar por nombre, ubicación o descripción..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    rounded="xl"
                    bg="gray.50"
                    border="2px solid"
                    borderColor="gray.200"
                    _hover={{ borderColor: 'gray.300' }}
                    _focus={{ borderColor: 'purple.500', bg: 'white' }}
                    fontSize="md"
                  />
                  {searchQuery && (
                    <InputRightElement>
                      <IconButton
                        size="sm"
                        variant="ghost"
                        onClick={() => setSearchQuery('')}
                        aria-label="Limpiar búsqueda"><FaTimes /></IconButton>
                    </InputRightElement>
                  )}
                </InputGroup>

                {/* Controles de filtros y ordenamiento */}
                <HStack w="full" justify="space-between" flexWrap="wrap" gap={2} data-tour="comunidad-filtros">
                  <HStack gap={3}>
                    <Button
                      variant={isFiltersOpen ? "solid" : "outline"}
                      colorPalette="purple"
                      size="sm"
                      onClick={onFiltersToggle}
                      rounded="full"><FaFilter />
                        Filtros
                        <Icon as={isFiltersOpen ? FaChevronUp : FaChevronDown} ml={2} /></Button>
                    
                    <Menu.Root>
                      <Menu.Trigger asChild><Button variant="outline" size="sm" rounded="full"><FaSortAmountDown />
                          {getSortLabel(sortBy)}
                        </Button></Menu.Trigger>
                      <Portal><Menu.Positioner><Menu.Content>
                            <Menu.Item onSelect={() => setSortBy('alphabetical_asc')} value='item-0'>
                              <Icon as={FaSortAmountDown} mr={2} />
                              Alfabético A-Z
                            </Menu.Item>
                            <Menu.Item onSelect={() => setSortBy('alphabetical_desc')} value='item-1'>
                              <Icon as={FaSortAmountUp} mr={2} />
                              Alfabético Z-A
                            </Menu.Item>
                            <Menu.Item onSelect={() => setSortBy('proximity')} value='item-2'>
                              <Icon as={FaMapMarkerAlt} mr={2} />
                              Más cerca de mí
                            </Menu.Item>
                            <Menu.Item onSelect={() => setSortBy('trusted_by')} value='item-3'>
                              <Icon as={FaHeart} mr={2} />
                              Más confiables
                            </Menu.Item>
                          </Menu.Content></Menu.Positioner></Portal>
                    </Menu.Root>
                  </HStack>

                  <HStack gap={2}>
                    <Text fontSize="sm" color="gray.600">
                      {filteredResults.length} resultados
                    </Text>
                    {(searchQuery ||
                      filters.communityEntityKind ||
                      filters.contratanteTipoEntidadId ||
                      filters.bandType ||
                      filters.proximity < 1200 ||
                      (filters.communityEntityKind === 'banda' &&
                        (filters.singleDate || filters.dateRange.start || filters.dateRange.end))) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        colorPalette="red"
                        onClick={clearFilters}
                      >
                        Limpiar filtros
                      </Button>
                    )}
                  </HStack>
                </HStack>
              </VStack>
            </Card.Body>
          </Card.Root>

          {/* Panel de filtros colapsable */}
          <Collapsible.Root open={isFiltersOpen}>
            <Collapsible.Content>
              <Card.Root bg="white" boxShadow="md" rounded="2xl">
                <Card.Body p={6}>
                  <SimpleGrid
                    columns={{
                      base: 1,
                      md: 2,
                      lg:
                        userType === 'banda'
                          ? filters.communityEntityKind === 'banda'
                            ? 3
                            : filters.communityEntityKind === 'contratante'
                              ? 3
                              : 2
                          : filters.communityEntityKind === 'banda'
                            ? 4
                            : filters.communityEntityKind === 'contratante'
                              ? 4
                              : 3,
                    }}
                    gap={6}
                  >
                    {/* Fechas disponibles: solo al filtrar por bandas (disponibilidad) */}
                    {filters.communityEntityKind === 'banda' && (
                      <Field.Root>
                        <Field.Label fontWeight="600" fontSize="sm">
                          <Icon as={FaCalendarAlt} mr={2} />
                          Fechas disponibles
                        </Field.Label>
                        <VStack gap={3} align="stretch">
                          {/* Selector de modo */}
                          <NativeSelect.Root>
                            <NativeSelect.Field
                              value={filters.dateMode}
                              onChange={(e) => handleFilterChange('dateMode', e.target.value)}
                              rounded="lg"
                              size="sm">
                              <option value="single">Día específico</option>
                              <option value="range">Rango de fechas</option>
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                          
                          {/* Input para día específico */}
                          {filters.dateMode === 'single' && (
                            <Input
                              type="date"
                              placeholder="Selecciona una fecha"
                              value={filters.singleDate}
                              onChange={(e) => handleFilterChange('singleDate', e.target.value)}
                              rounded="lg"
                              size="sm"
                            />
                          )}
                          
                          {/* Inputs para rango de fechas */}
                          {filters.dateMode === 'range' && (
                            <VStack gap={2}>
                              <Input
                                type="date"
                                placeholder="Fecha desde"
                                value={filters.dateRange.start}
                                onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, start: e.target.value })}
                                rounded="lg"
                                size="sm"
                              />
                              <Input
                                type="date"
                                placeholder="Fecha hasta"
                                value={filters.dateRange.end}
                                onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, end: e.target.value })}
                                rounded="lg"
                                size="sm"
                              />
                            </VStack>
                          )}
                        </VStack>
                      </Field.Root>
                    )}

                    {/* Banda vs entidad contratante - Solo visible cuando NO se busca desde un evento */}
                    {!eventName && (
                      <Field.Root>
                        <Field.Label fontWeight="600" fontSize="sm">
                          <Icon as={FaMusic} mr={2} />
                          Tipo de entidad
                        </Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            value={filters.communityEntityKind}
                            onChange={(e) => handleFilterChange('communityEntityKind', e.target.value)}
                            rounded="lg"
                            size="sm">
                            <option value="">Todos</option>
                            <option value="banda">Banda</option>
                            <option value="contratante">Entidad contratante</option>
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                    )}

                    {!eventName && filters.communityEntityKind === 'contratante' && (
                      <Field.Root>
                        <Field.Label fontWeight="600" fontSize="sm">
                          <Icon as={FaBuilding} mr={2} />
                          Tipo de entidad contratante
                        </Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            value={filters.contratanteTipoEntidadId}
                            onChange={(e) => handleFilterChange('contratanteTipoEntidadId', e.target.value)}
                            rounded="lg"
                            size="sm">
                            <option value="">Todos los tipos</option>
                            {tiposEntidadContratante.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.nombre}
                              </option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                    )}

                    {/* Tipo de banda (solo visible si se selecciona "banda") */}
                    {filters.communityEntityKind === 'banda' && (
                      <Field.Root>
                        <Field.Label fontWeight="600" fontSize="sm">
                          <Icon as={FaMusic} mr={2} />
                          Tipo de banda
                        </Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            placeholder="Todos los tipos de banda"
                            value={filters.bandType}
                            onChange={(e) => handleFilterChange('bandType', e.target.value)}
                            rounded="lg"
                            size="sm">
                            <option value="Banda de música">Banda de música</option>
                            <option value="Banda de Cornetas y Tambores">Banda de Cornetas y Tambores</option>
                            <option value="Agrupación Musical">Agrupación Musical</option>
                            <option value="Charanga">Charanga</option>
                            <option value="Música de Capilla">Música de Capilla</option>
                            <option value="Otro">Otro</option>
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                    )}

                    {/* Proximidad */}
                    <Field.Root>
                      <Field.Label fontWeight="600" fontSize="sm">
                        <Icon as={FaMapMarkerAlt} mr={2} />
                        Proximidad: {proximitySlider >= 1200 ? '🌍 Sin límite' : `${proximitySlider} km`}
                      </Field.Label>
                      {needsGuestProximityOrigin && (
                        <Field.HelperText mb={2}>
                          {guestProximityBlocked
                            ? 'Indica una dirección en España para filtrar por distancia (sin cuenta no usamos tu ubicación automática).'
                            : `Referencia: ${guestRefLabel.length > 90 ? `${guestRefLabel.slice(0, 90)}…` : guestRefLabel}`}
                        </Field.HelperText>
                      )}
                      {guestProximityBlocked ? (
                        <VStack align="stretch" gap={3}>
                          <Button size="sm" colorPalette="purple" onClick={onGuestProximityModalOpen}><Icon as={FaMapMarkerAlt} />Indicar dirección de referencia
                                                    </Button>
                          <Box
                            px={3}
                            py={2}
                            rounded="md"
                            borderWidth="1px"
                            borderColor="gray.200"
                            bg="gray.50"
                            opacity={0.85}
                            cursor="pointer"
                            onClick={onGuestProximityModalOpen}
                            aria-label="Abrir búsqueda de dirección para proximidad"
                          >
                            <SliderRoot
                              min={10}
                              max={1200}
                              step={10}
                              value={proximitySlider}
                              onValueChange={() => {}}
                              colorPalette="purple"
                              pointerEvents="none"
                            >
                              <SliderTrack><SliderFilledTrack /></SliderTrack>
                              <SliderThumb />
                            </SliderRoot>
                          </Box>
                          <Text fontSize="xs" color="gray.500">
                            Toca el botón o la barra para buscar tu dirección con OpenStreetMap.
                          </Text>
                        </VStack>
                      ) : (
                        <>
                          <Box px={3} py={2}>
                            <SliderRoot
                              min={10}
                              max={1200}
                              step={10}
                              value={proximitySlider}
                              onValueChange={(v) => setProximitySlider(v)}
                              colorPalette="purple"
                            >
                              <SliderTrack><SliderFilledTrack /></SliderTrack>
                              <SliderThumb />
                            </SliderRoot>
                          </Box>
                          {needsGuestProximityOrigin && hasGuestProximityRef && (
                            <Button
                              size="xs"
                              variant='plain'
                              colorPalette="purple"
                              alignSelf="flex-start"
                              onClick={onGuestProximityModalOpen}
                            >
                              Cambiar dirección de referencia
                            </Button>
                          )}
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            {proximitySlider >= 1200
                              ? 'Buscando en toda la península'
                              : `Buscando en un radio de ${proximitySlider} km`}
                          </Text>
                        </>
                      )}
                    </Field.Root>

                  </SimpleGrid>
                </Card.Body>
              </Card.Root>
            </Collapsible.Content>
          </Collapsible.Root>

          {/* Resultados */}
          {isLoading ? (
            <Flex justify="center" py={12}>
              <VStack gap={4}>
                <Spinner size="xl" color="purple.500" />
                <Text color="gray.600">Buscando...</Text>
              </VStack>
            </Flex>
          ) : filteredResults.length === 0 ? (
            <Card.Root bg="white" boxShadow="md" rounded="2xl">
              <Card.Body p={8} textAlign="center">
                <VStack gap={4}>
                  <Icon as={FaSearch} size="3xl" color="gray.400" />
                  <Heading size="md" color="gray.600">
                    No se encontraron resultados
                  </Heading>
                  <Text color="gray.500">
                    Intenta ajustar tus filtros o buscar con otros términos
                  </Text>
                  <Button
                    colorPalette="purple"
                    variant="outline"
                    onClick={clearFilters}
                    rounded="full"
                  >
                    Limpiar filtros
                  </Button>
                </VStack>
              </Card.Body>
            </Card.Root>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
              {filteredResults.map((item) => (
                <Card.Root
                  key={item.id}
                  bg="white"
                  boxShadow="md"
                  rounded="2xl"
                  overflow="hidden"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{ 
                    transform: 'translateY(-4px)', 
                    boxShadow: 'xl' 
                  }}
                  onClick={(e) => {
                    // Solo abrir modal si no está en modal o si se hace clic fuera de los botones
                    if (!inModal && !e.target.closest('button')) {
                      handleViewProfile(item);
                    }
                  }}
                  borderWidth={shortlistedIds.includes(item.id) ? "3px" : "0px"}
                  borderColor="green.400"
                >
                  <Box position="relative">
                    <EntityLogoImage
                      src={item.image}
                      alt={item.name}
                      name={item.name}
                      h="200px"
                      w="full"
                      circleSize="150px"
                    />
                    <Badge
                      position="absolute"
                      top={3}
                      right={3}
                      colorPalette={item.type === 'banda' ? 'purple' : 'orange'}
                      rounded="full"
                      px={3}
                      py={1}
                      maxW="72%"
                      display="inline-flex"
                      alignItems="center"
                      title={getCommunityCardTypeLabel(item)}
                    >
                      <Icon as={getCommunityCardTypeIcon(item)} flexShrink={0} mr={1} />
                      <Box as="span" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                        {getCommunityCardTypeLabel(item)}
                      </Box>
                    </Badge>
                    {shortlistedIds.includes(item.id) && (
                      <Badge
                        position="absolute"
                        top={3}
                        left={3}
                        colorPalette="yellow"
                        rounded="full"
                        px={3}
                        py={1}
                      >
                        <Icon as={FaStar} mr={1} />
                        Me interesa
                      </Badge>
                    )}
                  </Box>
                  
                  <Card.Body p={5} display="flex" flexDirection="column">
                    <VStack align="start" gap={3} flex="1">
                      <Box w="full">
                        <HStack gap={2} mb={1}>
                          <Heading size="md" lineClamp={1}>
                            {item.name}
                          </Heading>
                          {item.verified && (
                            <Tooltip content="Perfil validado" showArrow>
                              <span>
                                <Icon as={FaCheckCircle} color="blue.500" w={4} h={4} flexShrink={0} />
                              </span>
                            </Tooltip>
                          )}
                        </HStack>
                        <HStack gap={2} mb={2}>
                          <Icon as={FaMapMarkerAlt} color="gray.500" size="sm" />
                          <Text fontSize="sm" color="gray.600">
                            {item.location}
                            {item.distance != null &&
                            Number.isFinite(Number(item.distance)) &&
                            Number(item.distance) > 0.05
                              ? ` • ${Number(item.distance).toFixed(1)} km`
                              : ''}
                          </Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.600" lineClamp={2}>
                          {item.description}
                        </Text>
                      </Box>

                      <VStack align="start" gap={2} w="full">
                        {item.reviews && (
                          <HStack gap={1}>
                            <Icon as={FaStar} color="yellow.400" size="sm" />
                            <Text fontSize="sm" fontWeight="600" color="yellow.600">
                              {item.reviews.rating}
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              ({item.reviews.totalReviews} reseñas)
                            </Text>
                          </HStack>
                        )}
                        {item.availableDates && item.availableDates.length > 0 && (
                          <HStack gap={1}>
                            <Icon as={FaCalendarAlt} color="purple.400" size="sm" />
                            <Text fontSize="xs" color="gray.600">
                              {item.availableDates.length} fechas disponibles
                            </Text>
                          </HStack>
                        )}
                      </VStack>
                    </VStack>

                    {/* Botones siempre al fondo */}
                    <Box w="full" mt={4}>
                      {inModal ? (
                        <HStack w="full" gap={2}>
                          <Button
                            flex={1}
                            colorPalette="purple"
                            size="sm"
                            rounded="full"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewProfile(item);
                            }}><FaEye />Ver perfil
                                                      </Button>
                          {onAddToShortlist && (
                            <Button
                              flex={1}
                              colorPalette={shortlistedIds.includes(item.id) ? "gray" : "green"}
                              size="sm"
                              rounded="full"
                              variant={shortlistedIds.includes(item.id) ? "outline" : "solid"}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!shortlistedIds.includes(item.id)) {
                                  handleOpenContactModal(item);
                                }
                              }}
                              disabled={shortlistedIds.includes(item.id)}><FaHeart />{shortlistedIds.includes(item.id) ? "Añadida" : "Me interesa"}</Button>
                          )}
                        </HStack>
                      ) : (
                        <HStack w="full" gap={2}>
                          <Button
                            w="full"
                            colorPalette="purple"
                            size="sm"
                            rounded="full"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewProfile(item);
                            }}><FaEye />Ver perfil
                                                      </Button>
                        </HStack>
                      )}
                    </Box>
                  </Card.Body>
                </Card.Root>
              ))}
            </SimpleGrid>
          )}
      </VStack>
    </Container>
  );

  const profileModalShareUrl = useMemo(() => {
    if (!selectedProfile?.id) return '';
    return buildPublicProfileShareUrl(
      selectedProfile.type === 'banda' ? 'banda' : 'cofradia',
      selectedProfile.id,
    );
  }, [selectedProfile]);

  // Modal de perfil de banda
  const profileModal = selectedProfile && (
    selectedProfile.type === 'banda' ? (
      <PublicProfilePreview
        isOpen={isProfileOpen}
        onClose={() => {
          onProfileClose();
          setSelectedProfile(null);
        }}
        profileData={selectedProfile}
        shareUrl={profileModalShareUrl}
      />
    ) : (
      <PublicProfilePreviewCofradia
        isOpen={isProfileOpen}
        onClose={() => {
          onProfileClose();
          setSelectedProfile(null);
        }}
        profileData={selectedProfile}
        shareUrl={profileModalShareUrl}
      />
    )
  );

  // Modal de confirmación de contacto
  const contactModal = (
    <Dialog.Root open={isContactModalOpen} size='xl' placement='center' onOpenChange={e => {
      if (!e.open) {
        onContactModalClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <VStack align="start" gap={2}>
                <HStack gap={3}>
                  <Avatar.Root size="md"><Avatar.Fallback name={selectedBandaForContact?.name} /><Avatar.Image src={selectedBandaForContact?.image} /></Avatar.Root>
                  <VStack align="start" gap={0}>
                    <Text fontSize="lg" fontWeight="bold">
                      Contactar con {selectedBandaForContact?.name}
                    </Text>
                    {eventName && (
                      <Text fontSize="sm" fontWeight="normal" color="gray.600">
                        Para el evento: {eventName}
                      </Text>
                    )}
                  </VStack>
                </HStack>
              </VStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body>
              <VStack gap={4} align="stretch">
                <Alert.Root status="info" rounded="lg">
                  <Alert.Indicator />
                  <Box>
                    <Text fontSize="sm">
                      Puedes <strong>añadir a favoritos</strong> (sin mensaje por ahora) o escribir abajo y usar{' '}
                      <strong>Contactar y añadir a favoritas</strong> para abrir el chat con un mensaje.
                    </Text>
                  </Box>
                </Alert.Root>
                
                <Field.Root>
                  <Field.Label>
                    Mensaje de contacto (se enviará solamente si haces click en &quot;Contactar y añadir a favoritas&quot;)
                  </Field.Label>
                  <Textarea
                    placeholder="Ejemplo: ¡Hola! Estoy organizando una procesión y me gustaría contar con vuestra banda. ¿Estaríais disponibles?"
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                    rows={5}
                    resize="vertical"
                  />
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    El mensaje solo se envía al chat al pulsar «Contactar y añadir a favoritas».
                  </Text>
                </Field.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer
              flexDirection="column"
              alignItems="stretch"
              gap={0}
              px={{ base: 4, md: 6 }}
              pt={2}
              pb={4}
            >
              <Box w="full" overflowX="auto" pb={1} css={{
                scrollbarGutter: 'stable'
              }}>
                <HStack
                  w="full"
                  minW="max-content"
                  justify="flex-end"
                  align="center"
                  gap={3}
                  flexWrap="nowrap"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    flexShrink={0}
                    onClick={onContactModalClose}
                    disabled={isContactingBanda}
                  >
                    Cancelar
                  </Button>
                  {onAddToShortlist ? (
                    <Tooltip content="Añade la banda a favoritos sin enviar mensaje al chat" showArrow positioning={{
                      placement: "top"
                    }}>
                      <Button
                        variant="outline"
                        colorPalette="purple"
                        size="sm"
                        flexShrink={0}
                        onClick={handleAddFavoritesOnly}
                        disabled={isContactingBanda}
                        loading={isContactingBanda}
                        loadingText="Añadiendo…"><Icon as={FaHeart} />Añadir a favoritos
                                          </Button>
                    </Tooltip>
                  ) : null}
                  <Tooltip content="Envía el mensaje al chat y la añade a favoritas" showArrow positioning={{
                    placement: "top"
                  }}>
                    <Button
                      colorPalette="green"
                      size="sm"
                      flexShrink={0}
                      onClick={handleConfirmContact}
                      disabled={!contactMessage.trim() || isContactingBanda}
                      loading={isContactingBanda}
                      loadingText="Enviando…"><Icon as={FaCheckCircle} />Contactar y añadir a favoritas
                                      </Button>
                  </Tooltip>
                </HStack>
              </Box>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );

  const guestProximityModal = (
    <Dialog.Root
      open={isGuestProximityModalOpen}
      size='lg'
      placement='center'
      scrollBehavior="inside"
      onOpenChange={e => {
        if (!e.open) {
          onGuestProximityModalClose();
        }
      }}
    >
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Dirección de referencia</Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body>
              <Text fontSize="sm" color="gray.600" mb={4}>
                Escribe tu dirección o ciudad y elige una sugerencia de la lista. El filtro de proximidad usará ese punto
                como origen (solo en este dispositivo, hasta que limpies filtros o recargues).
              </Text>
              <Field.Root>
                <Field.Label fontSize="sm">Buscar en España</Field.Label>
                <Box position="relative">
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      value={proximityModalQuery}
                      onChange={(e) => setProximityModalQuery(e.target.value)}
                      placeholder="Calle, número, localidad…"
                      onFocus={() =>
                        proximityModalSuggestions.length > 0 && setShowProximityModalSuggestions(true)
                      }
                      autoFocus
                    />
                    {isSearchingProximityModal && (
                      <InputRightElement>
                        <Spinner size="sm" color="purple.500" />
                      </InputRightElement>
                    )}
                  </InputGroup>
                  {showProximityModalSuggestions && proximityModalSuggestions.length > 0 && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      mt={1}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      shadow="lg"
                      maxH="280px"
                      overflowY="auto"
                      zIndex={1000}
                    >
                      {proximityModalSuggestions.map((suggestion, index) => (
                        <Box
                          key={`${suggestion.place_id ?? suggestion.osm_id ?? index}`}
                          p={3}
                          cursor="pointer"
                          _hover={{ bg: 'gray.50' }}
                          onClick={() => selectGuestProximitySuggestion(suggestion)}
                          borderBottom="1px solid"
                          borderColor="gray.100"
                          _last={{ borderBottom: 'none' }}
                        >
                          <HStack gap={2} align="start">
                            <Icon as={FaMapMarkerAlt} color="purple.500" mt={0.5} flexShrink={0} />
                            <Text fontSize="sm">{suggestion.display_name}</Text>
                          </HStack>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <Field.HelperText>Sugerencias vía OpenStreetMap (Nominatim).</Field.HelperText>
              </Field.Root>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" onClick={onGuestProximityModalClose}>
                Cerrar
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );

  // Si está en modal, retornar solo el contenido
  if (inModal) {
    return (
      <>
        {searchContent}
        {profileModal}
        {contactModal}
        {guestProximityModal}
      </>
    );
  }

  // Si no está en modal, envolver en AppLayout
  return (
    <AppLayout
      headerVariant={searchHeaderVariant}
      userType={userType}
      userName={getPerfilDisplayName(profile, userType)}
    >
      {searchContent}
      {profileModal}
      {contactModal}
      {guestProximityModal}
    </AppLayout>
  );
};

export default Search;
